import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { KeyRound, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, useApi } from "@/lib/api";
import { dt, ROLE_LABEL } from "@/lib/format";
import { useMe } from "@/context/MeContext";

const RoleSelect = ({ value, roles, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-56" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}</SelectContent>
  </Select>
);

const AddUser = ({ roles, onClose, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState({ email: "", password: "", role: "member" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/users", f);
      toast.success(r.data.existing ? "Bestehendes Konto freigeschaltet (Passwort unverändert)" : "Benutzer angelegt");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="border-slate-200 bg-white" data-testid="dialog-add-user">
        <DialogHeader><DialogTitle>Benutzer anlegen</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>E-Mail-Adresse (= Benutzername)</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="input-new-user-email" /></div>
          <div><Label>Passwort (mind. 8 Zeichen)</Label><Input type="password" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="input-new-user-password" /></div>
          <div><Label>Rolle</Label><RoleSelect value={f.role} roles={roles} onChange={(v) => setF({ ...f, role: v })} testId="select-new-user-role" /></div>
          <p className="text-xs text-slate-500">Teilen Sie E-Mail und Passwort dem Benutzer mit. Bei der ersten Anmeldung auf einem neuen Gerät kann ein Bestätigungscode per E-Mail kommen.</p>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="btn-save-new-user">Anlegen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SetPassword = ({ user, onClose }) => {
  const api = useApi();
  const [pw, setPw] = useState("");
  const save = async () => {
    try { await api.post(`/users/${user.user_id}/password`, { password: pw }); toast.success("Passwort geändert"); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="border-slate-200 bg-white" data-testid="dialog-set-password">
        <DialogHeader><DialogTitle>Neues Passwort für {user.email}</DialogTitle></DialogHeader>
        <Input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="mind. 8 Zeichen" data-testid="input-set-password" />
        <DialogFooter><Button onClick={save} data-testid="btn-save-password">Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Benutzer() {
  const api = useApi();
  const me = useMe();
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [pwUser, setPwUser] = useState(null);

  const load = useCallback(() => api.get("/users").then((r) => setData(r.data)).catch(() => {}), [api]);
  useEffect(() => { if (me.role === "admin") load(); }, [load, me.role]);

  if (me.role !== "admin") return <Navigate to="/" replace />;
  if (!data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>;

  const changeRole = async (u, role) => {
    try { await api.patch(`/users/${u.user_id}/role`, { role }); toast.success("Rolle geändert"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Benutzerverwaltung</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Benutzer = E-Mail-Adresse. Nur hier angelegte oder freigeschaltete Konten erhalten Zugriff. „Gesperrt“ entzieht den Zugang sofort.</p>
        </div>
        <Button onClick={() => setAdding(true)} data-testid="btn-add-user"><UserPlus className="mr-2 h-4 w-4" /> Benutzer anlegen</Button>
      </div>
      <div className="panel overflow-x-auto p-5">
        <table className="w-full min-w-[640px] text-sm" data-testid="table-user-management">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="pb-3">E-Mail</th><th className="pb-3">Rolle</th><th className="pb-3">Angelegt</th><th className="pb-3 text-right">Passwort</th></tr></thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.user_id} className="border-t border-slate-200" data-testid={`row-user-${u.email}`}>
                <td className="py-3 font-medium">{u.email || u.user_id}{u.user_id === me.user_id && <span className="ml-2 text-xs text-orange-700">(Sie)</span>}</td>
                <td className="py-3"><RoleSelect value={u.role} roles={data.roles} onChange={(v) => changeRole(u, v)} testId={`select-role-${u.email}`} /></td>
                <td className="py-3 text-slate-500">{dt(u.created_at)}</td>
                <td className="py-3 text-right"><Button variant="ghost" size="sm" onClick={() => setPwUser(u)} data-testid={`btn-set-password-${u.email}`}><KeyRound className="mr-2 h-4 w-4" /> Neu setzen</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {adding && <AddUser roles={data.roles} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {pwUser && <SetPassword user={pwUser} onClose={() => setPwUser(null)} />}
    </div>
  );
}
